import { Box } from '@mui/material';
import React, { createContext, ReactNode, useEffect, useRef, useState } from 'react';

export const QuoteFooterContext = createContext<{ container: HTMLDivElement | null; setContainer: (node: HTMLDivElement | null) => void; fixedDocument?: boolean }>({ container: null, setContainer: () => {} });
export type QuoteEditorLifecycle = 'new' | 'saved';

interface QuoteEditorSurfaceProps {
  children: ReactNode;
  lifecycle: QuoteEditorLifecycle;
  fixedDocument?: boolean;
}

/** Shared document surface; the action rail stays outside its scrolling canvas. */
export default function QuoteEditorSurface({ children, lifecycle, fixedDocument = false }: QuoteEditorSurfaceProps) {
  const workspaceRef = useRef<HTMLDivElement>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [footerHeight, setFooterHeight] = useState(112);
  useEffect(() => {
    const footer = container?.closest('[data-testid="quote-editor-footer-bar"]');
    if (!footer || typeof ResizeObserver === 'undefined') return;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setFooterHeight(footer.getBoundingClientRect().height));
    });
    observer.observe(footer);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [container]);
  useEffect(() => {
    if (!fixedDocument || !workspaceRef.current) return;
    // Extend the existing page background behind horizontally scrolling content.
    for (let parent = workspaceRef.current.parentElement; parent; parent = parent.parentElement) {
      const style = getComputedStyle(parent);
      if (style.backgroundImage !== 'none' || (style.backgroundColor && !['transparent', 'rgba(0, 0, 0, 0)'].includes(style.backgroundColor))) {
        workspaceRef.current.style.backgroundImage = style.backgroundImage;
        workspaceRef.current.style.backgroundColor = style.backgroundColor;
        break;
      }
    }
  }, [fixedDocument]);
  return (
    <QuoteFooterContext.Provider value={{ container, setContainer, fixedDocument }}>
      <Box ref={workspaceRef} data-testid="shared-quote-editor"
        data-quote-lifecycle={lifecycle}
        data-fixed-document-workspace={fixedDocument || undefined}
        sx={{ display: fixedDocument ? 'block' : 'contents' }}>
        {children}
        <Box aria-hidden="true" sx={{ height: footerHeight }} />
      </Box>
    </QuoteFooterContext.Provider>
  );
}
